import React, { useRef, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useResizeObserver } from '@wix/da-hooks/pkg/useResizeObserver';
import { useScrollAware } from '@wix/da-hooks/pkg/useScrollAware';
import StickyContent from './StickyContent';

export interface Props {
  children(isStuck: boolean): React.ReactNode;
  zIndex?: number;
  marginTop?: number;
  marginLeft?: number | 'preserve';
  className?: string;
  bottomStop?: number;
  usePortal?: boolean;
  scrollNode?: HTMLElement | null;
  observeHeightResize?: boolean;
}

function getHeight(node: HTMLDivElement | null) {
  if (!node) {
    return 0;
  }

  return node.getBoundingClientRect().height;
}

const EMPTY_REF = { current: null } as React.RefObject<HTMLElement | null>;

const Sticky: React.FC<Props> = ({
  marginTop = 0,
  marginLeft: marginLeftProp = 0,
  bottomStop = 0,
  usePortal,
  scrollNode,
  observeHeightResize,
  ...contentProps
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState(getHeight(innerRef.current));
  const [top, setTop] = useState(0);
  const [marginLeft, setMarginLeft] = useState(
    typeof marginLeftProp === 'number' ? marginLeftProp : 0
  );

  const checkIfStuck = useCallback(() => {
    if (!rootRef.current) {
      return false;
    }
    const rootRect = rootRef.current.getBoundingClientRect();
    return rootRect.top - marginTop <= 0;
  }, [marginTop]);
  const [isStuck, setIsStuck] = useState(checkIfStuck());

  const updateTop = useCallback(
    ({ scrollTop, scrollHeight }) => {
      const isStuckNow = checkIfStuck();
      setIsStuck(isStuckNow);
      const height = minHeight || getHeight(innerRef.current);
      setMinHeight(height);

      if (isStuckNow && bottomStop !== 0) {
        const diff = scrollHeight - scrollTop - height - marginTop - bottomStop;
        setTop(diff < 0 ? diff : 0);
        return;
      }
      setTop(0);
    },
    [checkIfStuck, minHeight, bottomStop, marginTop]
  );

  useScrollAware(updateTop, scrollNode ?? undefined);

  useResizeObserver(
    observeHeightResize ? innerRef : EMPTY_REF,
    useCallback(() => {
      const currentHeight = getHeight(innerRef.current);
      if (minHeight !== currentHeight) {
        setMinHeight(currentHeight);
      }
    }, [minHeight])
  );

  useEffect(() => {
    if (marginLeftProp === 'preserve' && innerRef.current) {
      const clientRectLeft = innerRef.current.getBoundingClientRect().left;
      setMarginLeft(clientRectLeft);
    }
  }, [marginLeftProp]);

  const content = (
    <StickyContent
      isStuck={isStuck}
      top={top}
      marginTop={marginTop}
      marginLeft={marginLeft}
      ref={innerRef}
      {...contentProps}
    />
  );

  return (
    <div ref={rootRef} style={{ minHeight }}>
      {usePortal && isStuck
        ? ReactDOM.createPortal(content, document.body)
        : content}
    </div>
  );
};
Sticky.displayName = 'Sticky';

export default Sticky;
